import { useCallback } from 'react';

import { useAppDispatch } from '@/store';
import { MIN_LEN } from '@/constants';
import { findMoveConflicts, isDescendantPath } from '@/store/selectors/sixDetail';
import { moveItemsThunk } from '@/store/thunks/sixDetail/moveItemsThunk';
import type { FileEntry } from '@/types/SixDetail.types';
import type { MoveItemsPayloadDTO } from '@/types/SixDetailDTOs';
import { showErrorToast, showSuccessToast } from '@/utils/toast.utils';

interface UseMoveItemsReturn {
  handleMoveItems: (itemIds: string[], targetFolderPath: string | undefined) => void;
}

export function useMoveItems(sixId: string, allFiles: FileEntry[]): UseMoveItemsReturn {
  const dispatch = useAppDispatch();

  const normalizePath = useCallback((path: string | undefined): string | undefined => {
    if (typeof path !== 'string') {
      return undefined;
    }

    const trimmedPath = path.replace(/\/+$/, '');
    if (trimmedPath.length === 0 || trimmedPath === '/') {
      return undefined;
    }

    return trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`;
  }, []);

  const normalizeDestinationPath = useCallback((path: string | undefined): string => normalizePath(path) ?? '/', [normalizePath]);

  const buildMovePayload = useCallback((itemIds: string[], targetFolderPath: string | undefined): MoveItemsPayloadDTO => {
    const selectedIds = new Set(itemIds);
    const movingItems = allFiles.filter((entry) => selectedIds.has(entry.id));
    const fileIds = movingItems
      .filter((entry) => entry.kind === 'file')
      .map((entry) => entry.id);
    const folderIds = movingItems
      .filter((entry) => entry.kind === 'folder')
      .map((entry) => entry.id);

    return {
      fileIds,
      folderIds,
      destinationPath: normalizeDestinationPath(targetFolderPath),
    };
  }, [allFiles, normalizeDestinationPath]);

  const handleMoveItems = useCallback(
    (itemIds: string[], targetFolderPath: string | undefined): void => {
      if (typeof sixId !== 'string' || sixId.trim().length < MIN_LEN) return;
      if (itemIds.length === 0) return;

      const movingItems = allFiles.filter((entry) => itemIds.includes(entry.id));
      const folderItems = movingItems.filter((entry) => entry.kind === 'folder');
      for (const folder of folderItems) {
        if (folder.path === targetFolderPath) return;
        if (targetFolderPath !== undefined && isDescendantPath(folder.path, targetFolderPath)) {
          showErrorToast('Cannot move a folder into its own subfolder');
          return;
        }
      }

      const conflicts = findMoveConflicts(allFiles, itemIds, targetFolderPath);
      if (conflicts.length > 0) {
        showErrorToast(`Cannot move — "${conflicts[0]}" already exists at destination`);
        return;
      }

      const payload = buildMovePayload(itemIds, targetFolderPath);
      if (payload.fileIds.length === 0 && payload.folderIds.length === 0) {
        return;
      }

      const successMessage = itemIds.length === 1
        ? 'Item moved successfully'
        : `${itemIds.length} items moved successfully`;
      const errorMessage = itemIds.length === 1
        ? 'Failed to move item'
        : 'Failed to move items';

      void dispatch(moveItemsThunk({ sixId, ...payload }))
        .unwrap()
        .then(() => {
          showSuccessToast(successMessage);
        })
        .catch(() => {
          showErrorToast(errorMessage);
        });
    },
    [allFiles, buildMovePayload, dispatch, sixId],
  );

  return { handleMoveItems };
}
